import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseMatchMediaService } from '@fuse/services/match-media.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { FuseMediaObserver } from '../../services/fuse-media-observer.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'app/core/services/local-storage.service';
import { NavigationItemInterface } from 'app/layout/components/navigation/models/navigation-item-interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { locale as english } from './i18n/en';
import { locale as french } from './i18n/fr';
import { locale as portuguese } from './i18n/pt';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDivider } from '@angular/material/divider';
import { MatNavList, MatListItem } from '@angular/material/list';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'fuse-shortcuts',
  styleUrls: ['./shortcuts.component.scss'],
  templateUrl: './shortcuts.component.html',
  imports: [
    MatIconButton,
    MatIcon,

    RouterLink,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatFormField,
    MatLabel,
    MatInput,
    MatDivider,
    MatNavList,
    MatListItem,
    TranslatePipe,
  ],
})
export class FuseShortcutsComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() public navigation: NavigationItemInterface[] | undefined;
  @ViewChild('searchInput') public searchInputField;
  @ViewChild('shortcuts') public shortcutsEl: ElementRef;
  public displayableShortcutItems: NavigationItemInterface[];
  public filteredNavigationItems: NavigationItemInterface[];
  public mobileShortcutsPanelActive: boolean;
  public navigationItems: NavigationItemInterface[];
  public searching: boolean;
  public shortcutItems: NavigationItemInterface[];

  private _unsubscribeAll: Subject<void>;

  public constructor(
    private _localStorageService: LocalStorageService,
    private _fuseMatchMediaService: FuseMatchMediaService,
    private _fuseNavigationService: FuseNavigationService,
    private _mediaObserver: FuseMediaObserver,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private _translateService: TranslateService,
  ) {
    // Set the defaults
    this.shortcutItems = [];
    this.displayableShortcutItems = [];
    this.searching = false;
    this.mobileShortcutsPanelActive = false;

    // Set the private defaults
    this._unsubscribeAll = new Subject<void>();

    this._fuseTranslationLoaderService.loadTranslations(french, english, portuguese);
  }

  public hideMobileShortcutsPanel(): void {
    this.mobileShortcutsPanelActive = false;
    this.shortcutsEl.nativeElement.classList.remove('show-mobile-panel');
  }

  public isInShortcuts(navigationItem: NavigationItemInterface): NavigationItemInterface | undefined {
    return this.displayableShortcutItems.find((item) => {
      return item.url === navigationItem.url;
    });
  }

  public ngAfterViewInit(): void {
    this.filterDisplayableShortcutItems();

    // Subscribe to media changes
    this._fuseMatchMediaService.onMediaChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      if (this._mediaObserver.isActive('gt-sm')) {
        this.hideMobileShortcutsPanel();
      }
    });
  }

  public ngOnChanges(): void {
    // Get the navigation items and flatten them
    this.filteredNavigationItems = this.navigationItems = this._fuseNavigationService.getFlatNavigation(this.navigation);
    this.filterDisplayableShortcutItems();
  }

  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  public ngOnInit(): void {
    const shortcutItemsString = this._localStorageService.read<string>('FUSE2.shortcuts');

    if (shortcutItemsString) {
      this.shortcutItems = JSON.parse(shortcutItemsString);
    } else {
      // Default user's shortcut items
      this.shortcutItems = [
        {
          id: 'item',
          translate: 'NAV.ITEMS',
          type: 'item',
          icon: 'layers',
          url: '/item',
        },
      ];
    }
  }

  public onMenuOpen(): void {
    setTimeout(() => {
      this.searchInputField.nativeElement.focus();
    });
  }

  public search(event): void {
    const value = event.target.value.toLowerCase();

    if (value === '') {
      this.searching = false;
      this.filteredNavigationItems = this.navigationItems;

      return;
    }

    this.searching = true;
    this.filteredNavigationItems = [];
    this.navigationItems.forEach((navigationItem) => {
      this._translateService.get(navigationItem.translate!).subscribe((translatedNavigationItem) => {
        if (translatedNavigationItem.toLowerCase().includes(value)) {
          this.filteredNavigationItems.push(navigationItem);
        }
      });
    });
  }

  public showMobileShortcutsPanel(): void {
    this.mobileShortcutsPanelActive = true;
    this.shortcutsEl.nativeElement.classList.add('show-mobile-panel');
  }

  public toggleShortcut(event, itemToToggle): void {
    event.stopPropagation();

    for (let i = 0; i < this.shortcutItems.length; i++) {
      if (this.shortcutItems[i].url === itemToToggle.url) {
        this.shortcutItems.splice(i, 1);
        this.filterDisplayableShortcutItems();

        // Save to the cookies
        this._localStorageService.write('FUSE2.shortcuts', JSON.stringify(this.shortcutItems));

        return;
      }
    }

    this.shortcutItems.push(itemToToggle);
    this.filterDisplayableShortcutItems();

    // Save to the cookies
    this._localStorageService.write('FUSE2.shortcuts', JSON.stringify(this.shortcutItems));
  }

  private filterDisplayableShortcutItems(): void {
    this.displayableShortcutItems = this.navigationItems.filter((filteredNavigationItem) =>
      this.shortcutItems.find((s) => s.id === filteredNavigationItem.id),
    );
  }
}
